import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, IconButton, Close, makeStyles, Box } from '@quillbot/design-system';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import { theme } from 'config/theme';
import Link from 'next/link';
import logger from 'core/logger/logger';
import Track from '@Common/handlers/track';
import Img from '@Common/components/img';
import { firebase } from 'config/firebase';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    // width: 320,
    borderRadius: 12,
  },
  dialogContent: {
    display: 'flex',
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  leftWrapper: {
    backgroundColor: theme.palette.primary.main,
    padding: '24px 0 24px 28px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      padding: '0 24px 48px',
      justifyContent: 'center',
    },
  },
  imgWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: -24,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginTop: '-120px',
    },
  },
  imgShadow: {
    width: 117,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.dark,
    marginTop: '16px',
    marginLeft: '12px',
    [theme.breakpoints.down('sm')]: {
      marginTop: '8px',
      marginLeft: '6px',
    },
  },
  modalImg: {
    height: 218,
    zIndex: 3,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      height: 180,
    },
  },
  highlightSection: {
    backgroundColor: theme.specialColors.LIGHT_BLUE_COLOR,
    padding: '8px 14px',
    fontSize: 16,
    fontWeight: '600',
    borderRadius: '5px',
    marginTop: 12,
    textAlign: 'left',
  },
  highlightList: {
    fontWeight: '400',
    paddingLeft: 16,
    margin: '2px 0 0',
  },
  rightWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    '&:before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      width: 0,
      height: '100%',
      // borderLeft: `60px solid ${theme.palette.primary.main}`,
      borderRight: '50px solid transparent',
      borderTop: `100px solid ${theme.palette.primary.main}`,
      borderBottom: `100px solid ${theme.palette.primary.main}`,
      zIndex: 1,
      [theme.breakpoints.down('sm')]: {
        height: 0,
        width: '100%',
        top: 'initial',
        bottom: 0,
        borderLeft: `120px solid ${theme.palette.primary.main}`,
        borderRight: `120px solid ${theme.palette.primary.main}`,
        borderBottom: 'none',
        borderTop: '50px solid transparent',
      },
    },
  },
  content: {
    padding: '28px 64px 64px 72px',
    [theme.breakpoints.down('sm')]: {
      padding: '36px 20px 140px',
      textAlign: 'center',
    },
  },
  topText: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: '1.3',
    color: theme.palette.primary.main,
    // textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      // textAlign: 'center',
    },
  },
  twitterIcon: {
    height: 18,
    marginRight: '8px',
    [theme.breakpoints.down('sm')]: {
      filter: 'brightness(100)',
    },
  },
  modalTextWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  modalText: {
    fontSize: 16,
    fontWeight: '500',
    maxWidth: 280,
    marginTop: 12,
  },
  actionBtn: {
    borderRadius: '100px',
    fontSize: 14,
    fontWeight: 700,
    padding: '5px 16px',
  },
  actionBtnWrapper: {
    marginTop: 24,
  },
  footer: {
    color: theme.palette.text.primary,
    backgroundColor: theme.specialColors.LIGHT_BLUE_COLOR,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    padding: '11px 16px 11px 148px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: '9px 16px',
      zIndex: '2',
      color: theme.specialColors.WHITE_TEXT,
      backgroundColor: theme.palette.primary.dark,
    },
  },
  closeBtn: {
    color: theme.specialColors.CHECKBOX_DEFAULT,
    position: 'absolute',
    right: '4px',
    top: '4px',
    zIndex: 3,
    '&:hover': {
      color: theme.specialColors.CHECKBOX_DEFAULT,
    },
  },
}));

function PromotionDialog() {
  const classes = useStyles();
  const router = useRouter();
  const isAuthenticated = useSelector((state: RootState) => state?.user?.isAuthenticated);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension) || false;
  const isNative = useSelector((state: RootState) => state.common.platform.native);
  const isPremium = useSelector((rootState: RootState) => rootState?.user?.premium);
  const independentTool = useSelector((rootState: RootState) => rootState?.common.independentTool);

  const submittedInputCount = +QuillLocalStorage.getInstance().get('submittedInputCount', '0');
  const [showModal, setShowModal] = useState(false);

  const saleRoutes = ['/'];

  const showDialog =
    saleRoutes.includes(router.pathname) &&
    isAuthenticated &&
    !isExtension &&
    !isNative &&
    !isPremium &&
    !independentTool &&
    !(QuillLocalStorage.getInstance().get('hideBackSchoolGiveaway', 'false') === 'true') &&
    submittedInputCount >= 100;

  const handleClose = () => {
    setShowModal(false);
    QuillLocalStorage.getInstance().set('hideBackSchoolGiveaway', 'true');
  };

  useEffect(() => {
    async function init() {
      const { getDatabase, onValue, ref } = await firebase.database;

      onValue(ref(getDatabase(), 'modalConfig'), (snapshot: { val: Function }) => {
        try {
          const data = snapshot.val();
          const modalConfig = { ...data };
          if (modalConfig.backSchoolGiveawayVisible === true) {
            setShowModal(showDialog || false);
            if (showDialog) {
              Track.modalStateChangeAction('shown', 'comn/user/interacted_backToSchool_giveaway_modal');
            }
          }
        } catch (e) {
          logger('FirebaseModalConfig', e);
        }
      });
    }

    init();
  }, [router.pathname]);

  return (
    <div>
      <Dialog
        maxWidth="md"
        open={showModal}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
            Track.modalStateChangeAction('closed', 'comn/user/interacted_backToSchool_giveaway_modal');
          }
        }}
        aria-labelledby="promotion-dialog"
        classes={{ paper: classes.dialogPaper }}>
        <DialogContent className={classes.dialogContent}>
          <IconButton
            className={classes.closeBtn}
            onClick={() => {
              handleClose();
              Track.modalStateChangeAction('closed', 'comn/user/interacted_backToSchool_giveaway_modal');
            }}>
            <Close />
          </IconButton>
          <div className={classes.leftWrapper}>
            <div className={classes.imgWrapper}>
              <Img src="/images/theme/commonImages/back-to-school.svg" alt="trophy" className={classes.modalImg} />
              <Box className={classes.imgShadow} />
            </div>
          </div>
          <div className={classes.rightWrapper}>
            <div className={classes.content}>
              <div className={classes.topText} id="promotion-dialog">
                Back-to-School <br />
                GIVEAWAY
              </div>
              <div className={classes.modalText}>
                Enter our <span style={{ color: '#1D9BF0', fontWeight: '700' }}>Twitter Giveaway</span> and win a
                Premium subscription
              </div>
              <div className={classes.highlightSection}>
                To enter:
                <ol className={classes.highlightList}>
                  <li>Follow us on Twitter</li>
                  <li>Like and retweet this tweet</li>
                </ol>
              </div>
              <div className={classes.actionBtnWrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.actionBtn}
                  onClick={() => {
                    Track.modalStateChangeAction('accepted', 'comn/user/interacted_backToSchool_giveaway_modal');
                    window.open('https://twitter.com/TheQuillBot/status/1562092382304325633');
                    handleClose();
                  }}>
                  Enter to Win
                </Button>
              </div>
            </div>
          </div>
          <div className={classes.footer}>
            <Img src="/images/theme/commonImages/twitter_blue.svg" alt="twitter" className={classes.twitterIcon} />{' '}
            Giveaway ends August 31<sup>st</sup>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PromotionDialog;
